import { createTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";

export const tableTheme = ({ palette, breakpoints }) =>
    createTheme({
        palette,
        breakpoints: {
            values: {
                xs: 0,
                sm: 768,
                md: 1440,
                lg: 1920,
                xl: 2560
            }
        },
        components: {
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        borderBottom: `1px solid ${grey[300]}`,
                        backgroundColor: grey[100],
                        height: "56px",
                        "& .MuiTableCell-root": {
                            fontWeight: 700
                        },
                        "& :first-child.MuiTableCell-root, ": {
                            borderRadius: "4px 0 0 0"
                        },
                        "& :last-child.MuiTableCell-root, ": {
                            borderRadius: "0 4px  0 0"
                        }
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        color: grey[700],
                        lineHeight: "20px",
                        verticalAlign: "middle",
                        padding: "7px 9px !important",
                        "&:first-child": {
                            padding: "7px 7px 7px 14px !important"
                        },
                        "&:last-child": {
                            padding: "7px 18px 7px 9px !important"
                        }
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        height: "56px",
                        "&.MuiTableRow-hover:hover": {
                            backgroundColor: palette.primary.light,
                            cursor: "pointer"
                        },
                        "&:last-child": {
                            "& .MuiTableCell-root": {
                                border: "none"
                            },
                            "& .MuiTableCell-root:first-of-type": {
                                borderBottomLeftRadius: "4px"
                            },
                            "& .MuiTableCell-root:last-of-type": {
                                borderBottomRightRadius: "4px"
                            }
                        }
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    body1: { fontSize: "14px" }
                },
                variants: [
                    {
                        props: { variant: "condensed" },
                        style: {
                            lineHeight: 1.4
                        }
                    },
                    {
                        props: { variant: "link" },
                        style: {
                            textDecoration: "underline",
                            "&:hover": {
                                cursor: "pointer"
                            }
                        }
                    }
                ]
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontWeight: 400,
                        fontSize: "14px",
                        margin: "2px !important"
                    }
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        color: "white"
                    }
                }
            }
        }
    });

export const tableConfigTheme = (palette) =>
    createTheme({
        palette,
        components: {
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        "& .MuiInputBase-root": {
                            paddingLeft: "18px !important",
                            paddingRight: "5px !important"
                        }
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        "&:hover fieldset": {
                            borderColor: `${palette.inputBorder.main} !important`
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: `${palette.primary.main} !important`
                        }
                    },
                    input: {
                        fontSize: "14px",
                        paddingTop: "8px !important",
                        paddingBottom: "8px !important",
                        paddingLeft: "0px !important"
                    }
                }
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        "&:hover": {
                            cursor: "pointer"
                        }
                    }
                }
            },
            MuiTypography: {
                styleOverrides: { h1: { fontSize: "24px", fontWeight: 400 } }
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontWeight: 400,
                        fontSize: "14px",
                        margin: "2px !important"
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        color: "white",
                        padding: "6px 20px",
                        height: "36px"
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        ":hover": {
                            cursor: "pointer"
                        }
                    }
                }
            }
        }
    });

export const dropDownMenuTheme = ({ palette }) =>
    createTheme({
        palette,
        typography: {
            fontSize: "14px"
        },
        components: {
            MuiList: {
                styleOverrides: {
                    root: {
                        padding: "6px 18px 13px 18px!important",
                        width: "175px"
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.inputBorder.main
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: palette.primary.main
                        }
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        minWidth: "175px"
                    }
                }
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        margin: "12px 0px 8px 0px"
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        height: "24px",
                        color: palette.text.main,
                        marginBottom: "0px",
                        "& .MuiButtonBase-root": {
                            padding: "1px 9px !important"
                        }
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        width: "1.1rem",
                        height: "1.1rem"
                    }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    subHeading: {
                        display: "block",
                        fontSize: "0.75rem",
                        fontWeight: "700",
                        lineHeight: "12px",
                        color: palette.helperText.main,
                        textTransform: "upperCase",
                        fontFamily: ["Roboto", "sans-serif"].join(",")
                    }
                }
            }
        }
    });

export const paginationTheme = ({ palette }) =>
    createTheme({
        palette,
        components: {
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        "& .MuiInputBase-input:focus": {
                            backgroundColor: "transparent"
                        }
                    }
                }
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        alignItems: "center",
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                            {
                                color: palette.text.main,
                                marginBottom: 0
                            },
                        "& .MuiTablePagination-actions": {
                            display: "flex",
                            gap: "0.5rem",
                            "& button": {
                                border: "1px solid rgba(0,0,0,0.2)",
                                borderRadius: "4px"
                            },
                            "& button:hover": {
                                backgroundColor: palette.lightGrey
                            }
                        }
                    }
                }
            },
            MuiTablePagination: {
                styleOverrides: {
                    actions: {
                        "& .MuiButtonBase-root": {
                            border: "none",
                            color: palette.primary.main
                        },
                        "& .Mui-disabled": {
                            color: grey[400]
                        }
                    },
                    menuItem: {
                        fontSize: "14px"
                    }
                }
            }
        }
    });
